import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useNotificationInbox } from '../context/NotificationInboxContext';
import { formatNotificationTime, notificationMeta } from '../utils/notificationInbox';

function NotificationRow({ item, onRead, compact = false }) {
  const meta = notificationMeta(item.type);
  const navigate = useNavigate();

  const handleClick = () => {
    if (!item.read) onRead(item._id);
    if (item.link) navigate(item.link);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`w-full text-left flex gap-3 px-4 py-3 transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/60 ${
        !item.read ? 'bg-blue-50/50 dark:bg-blue-950/20' : ''
      }`}
    >
      <span
        className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-base ${meta.bg}`}
        aria-hidden
      >
        {meta.icon}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className={`text-sm font-semibold truncate ${!item.read ? 'text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'}`}>
            {item.title}
          </p>
          {!item.read && (
            <span className="shrink-0 w-2 h-2 mt-1.5 rounded-full bg-blue-500" aria-label="Unread" />
          )}
        </div>
        {!compact && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2 leading-relaxed">
            {item.message}
          </p>
        )}
        <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">{formatNotificationTime(item.createdAt)}</p>
      </div>
    </button>
  );
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { unreadCount, recentItems, loading, refresh, markRead, markAllRead } = useNotificationInbox();

  useEffect(() => {
    const onOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, []);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  const displayCount = unreadCount > 99 ? '99+' : unreadCount;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ''}`}
        aria-expanded={open}
      >
        <svg className="w-6 h-6 text-gray-900 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold leading-none">
            {displayCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[min(100vw-2rem,22rem)] bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white">Notifications</h3>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[min(24rem,60vh)] overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700/80">
            {loading && !recentItems.length ? (
              <div className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">Loading…</div>
            ) : recentItems.length === 0 ? (
              <div className="py-10 px-4 text-center">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">No notifications yet</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Ticket rewards, game updates, and reminders appear here.
                </p>
              </div>
            ) : (
              recentItems.map((item) => (
                <NotificationRow key={item._id} item={item} onRead={markRead} compact />
              ))
            )}
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 p-2">
            <Link
              to="/notifications"
              onClick={() => setOpen(false)}
              className="block w-full text-center py-2.5 text-sm font-semibold text-blue-600 dark:text-blue-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg transition-colors"
            >
              View all notifications
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
